import getWeekOfMonth from 'date-fns/getWeekOfMonth/index';
import getDay from 'date-fns/getDay/index';
import { isNotNullish } from '@front-app/util/types';
import { DigestMonthlyTiming, DigestTimingDay, MonthlyTimingKey } from '../model';

export const allMonthlyTimingKeys: MonthlyTimingKey[] = [
	'firstWeek',
	'secondWeek',
	'thirdWeek',
	'fourthWeek',
	'lastWeek',
];

export const orderedWeekDays = [
	DigestTimingDay.SUNDAY,
	DigestTimingDay.MONDAY,
	DigestTimingDay.TUESDAY,
	DigestTimingDay.WEDNESDAY,
	DigestTimingDay.THURSDAY,
	DigestTimingDay.FRIDAY,
	DigestTimingDay.SATURDAY,
];

export enum DigestScheduleType {
	UNSCHEDULED = 'unscheduled',
	PERIODIC = 'periodic',
	WEEKLY_SAME_DAY = 'weekly_same_day',
	MONTHLY_SAME_DAY = 'monthly_same_day',
	CUSTOM = 'custom',
}

export function determineScheduleType(monthlyTiming: DigestMonthlyTiming, periodicTiming: number | undefined): DigestScheduleType {
	if (isNotNullish(periodicTiming) && periodicTiming > 0) {
		return DigestScheduleType.PERIODIC;
	}
	if (isUnscheduled(monthlyTiming)) {
		return DigestScheduleType.UNSCHEDULED;
	}
	if (isWeeklySameDaySchedule(monthlyTiming)) {
		return DigestScheduleType.WEEKLY_SAME_DAY;
	}
	if (isMonthlySameDaySchedule(monthlyTiming)) {
		return DigestScheduleType.MONTHLY_SAME_DAY;
	}
	return DigestScheduleType.CUSTOM;
}

/**
 * Should return true if the no every week is empty
 */
function isUnscheduled(monthlyTiming: DigestMonthlyTiming): boolean {
	return allMonthlyTimingKeys.every((week) => monthlyTiming[week].size === 0);
}

/**
 * Should return true is every week has 1 day, and it is the same day
 */
function isWeeklySameDaySchedule(monthlyTiming: DigestMonthlyTiming): boolean {
	let lastWeeksDay = null;
	// eslint-disable-next-line no-restricted-syntax
	for (const week of allMonthlyTimingKeys) {
		const currentWeeksDays = monthlyTiming[week];
		if (currentWeeksDays.size !== 1) {
			return false;
		}
		const nextVal = Array.from(currentWeeksDays.values())[0];
		if (nextVal === undefined || (lastWeeksDay != null && lastWeeksDay !== nextVal)) {
			return false;
		}
		lastWeeksDay = nextVal;
	}
	return true;
}

/**
 * Should return true if only 1 day is selected in the month
 */
function isMonthlySameDaySchedule(monthlyTiming: DigestMonthlyTiming): boolean {
	return getAllFlattenedDays(monthlyTiming).length === 1;
}

export function getAllFlattenedDays(monthlyTiming: DigestMonthlyTiming): DigestTimingDay[] {
	return allMonthlyTimingKeys.flatMap((week) => Array.from(monthlyTiming[week].values()));
}

export function getFirstWeekWithDays(monthlyTiming: DigestMonthlyTiming): keyof DigestMonthlyTiming | undefined {
	return allMonthlyTimingKeys.find((week) => monthlyTiming[week].size > 0);
}

export function getMonthWeekFromDate(date: Date): MonthlyTimingKey {
	return allMonthlyTimingKeys[getWeekOfMonth(date) - 1];
}

export function getWeekDayFromDate(date: Date): DigestTimingDay {
	return orderedWeekDays[getDay(date)];
}

export function getWeeklySameDayTiming(day: DigestTimingDay): DigestMonthlyTiming {
	const timing = createBlankMonthlyTiming();
	allMonthlyTimingKeys.forEach((week) => timing[week].add(day));
	return timing;
}

export function getMonthlySameDayTiming(week: MonthlyTimingKey, day: DigestTimingDay): DigestMonthlyTiming {
	const timing = createBlankMonthlyTiming();
	timing[week].add(day);
	return timing;
}

export function createBlankMonthlyTiming(): DigestMonthlyTiming {
	return {
		firstWeek: new Set(),
		secondWeek: new Set(),
		thirdWeek: new Set(),
		fourthWeek: new Set(),
		lastWeek: new Set(),
	};
}
